import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronsUpDown } from 'lucide-react'
import { TaskDropdown } from '../TaskDropdown'
import { RewardPickerContent } from '../RewardPicker'
import { RewardDropdownContent } from '../RewardDropdown'
import { RewardCatalogIcon } from '@/src/renderer/components/ui/RewardCatalogIcon'
import { SQUIRCLE_PATH } from '@/src/renderer/components/ui/RewardCatalogIcon/squircle'
import { MotivationIcon } from '@/src/renderer/components/icons'
import { getRewardById, rewardCta, type CatalogLanguage } from '@/src/renderer/lib/data/rewardCatalog'
import type { TaskRewardTriggerProps } from './TaskRewardTrigger.types'
import './TaskRewardTrigger.scss'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'

/**
 * The reward control in the expanded task's content area (Figma 2172-17470 /
 * 2172-15068). Reads as a select, not as a badge: leading squircle, the reward
 * phrased as an action ("Süße Nascherei zwischendurch"), trailing chevrons.
 *
 * It is always visible — with no reward attached it shows the gift outline and
 * "Belohnung / Ziel", and it is the only entry point since the gift button left
 * the action bar. Detaching a reward happens in the picker's trash button; the
 * trigger deliberately carries no destructive affordance.
 *
 * Without a reward the popover opens on the two-entry menu ("Belohnung
 * hinzufügen" / "Mein Ziel dahinter"); with one it goes straight to the picker.
 *
 * @example
 * <TaskRewardTrigger rewardId={reward} onValueChange={handleRewardChange}
 *   open={open} onOpenChange={setOpen} align="end" />
 */
export const TaskRewardTrigger: React.FC<TaskRewardTriggerProps> = ({
  rewardId, onValueChange, open, onOpenChange, align,
}) => {
  const { t, i18n } = useTranslation()
  const language: CatalogLanguage = i18n.language?.startsWith('en') ? 'en' : 'de'

  const reward = getRewardById(rewardId)
  const label = reward ? rewardCta(reward, language) : t('taskCreation.rewardTrigger.placeholder')

  // Which of the two screens the single popover shows. An attached reward skips
  // the menu entirely — the ref mirrors the state so the close handler below can
  // read it synchronously.
  const [view, setView] = useState<'menu' | 'picker'>(reward ? 'picker' : 'menu')
  const viewRef = useRef(view)
  viewRef.current = view

  // A reward attached from elsewhere (context menu, collapsed row) must not
  // leave the popover pointing at the menu the next time it opens.
  useEffect(() => {
    if (open) return
    const next = reward ? 'picker' : 'menu'
    setView(next)
    viewRef.current = next
  }, [open, reward])

  const handleSelect = useCallback((action: 'reward' | 'goal') => {
    if (action === 'reward') {
      // Swap the screen instead of closing — a second popover would need its
      // own anchor and would re-open with a visible flicker.
      setView('picker')
      viewRef.current = 'picker'
      return
    }
    // "Mein Ziel dahinter" has no flow behind it yet; closing is all it does,
    // exactly as in the action bar it replaces.
    onOpenChange(false)
  }, [onOpenChange])

  // The menu calls onClose right after onSelect; swallow that one call when we
  // just switched to the picker, or the popover would close under it.
  const handleMenuClose = useCallback(() => {
    if (viewRef.current === 'picker') return
    onOpenChange(false)
  }, [onOpenChange])

  const handlePicked = useCallback((picked: string | null) => {
    onValueChange(picked)
    onOpenChange(false)
  }, [onValueChange, onOpenChange])

  return (
    <div className={`task-reward-trigger${align === 'end' ? ' task-reward-trigger--end' : ''}${open ? ' task-reward-trigger--open' : ''}`}>
      <TaskDropdown
        open={open}
        onOpenChange={onOpenChange}
        side="bottom"
        align={align === 'end' ? 'end' : 'start'}
        trigger={
          <WithTooltip content={label}>
            <button type="button" className="task-reward-trigger__button">
              {reward ? (
                <RewardCatalogIcon rewardId={reward.id} name="" size={28} />
              ) : (
                <span className="task-reward-trigger__empty-icon">
                  {/* Same squircle as the catalogue artwork, drawn as an outline
                      so the empty slot reads as "a reward belongs here".
                      **Rand im Zeichenfeld** (gemeldet 04.10.2026: ungleichmäßiger Rahmen): Die Form reicht
                      von 0 bis 40 — bei `0 0 40 40` fiel an den geraden Kanten die äußere Hälfte der Linie
                      weg, an den Ecken nicht. 0,74 Rand legt die 1-px-Linie bei 28 px auf ganze Pixel
                      (40 · 28 / 41,48 = 27 → Kanten bei 0,5 und 27,5). */}
                  <svg width={28} height={28} viewBox="-0.74 -0.74 41.48 41.48" fill="none" aria-hidden="true">
                    <path d={SQUIRCLE_PATH} stroke="currentColor" strokeWidth={1} vectorEffect="non-scaling-stroke" />
                  </svg>
                  <MotivationIcon size={12} className="task-reward-trigger__empty-glyph" />
                </span>
              )}
              <span className={`task-reward-trigger__label${reward ? '' : ' task-reward-trigger__label--placeholder'}`}>
                {label}
              </span>
              <ChevronsUpDown size={16} className="task-reward-trigger__chevron" />
            </button>
          </WithTooltip>
        }
      >
        {view === 'picker' ? (
          <RewardPickerContent
            value={rewardId}
            onValueChange={handlePicked}
            onClose={() => onOpenChange(false)}
            placement="badge"
          />
        ) : (
          <RewardDropdownContent
            onSelect={handleSelect}
            onClose={handleMenuClose}
            placement="badge"
          />
        )}
      </TaskDropdown>
    </div>
  )
}
